/**
 * Allys Sort Control
 * 
 * Enhanced sort dropdown with Allys theme styling and micro-interactions
 * Features modern aesthetic with icons, gradients, and smooth animations
 */

'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';

export interface SortOption<T = unknown> {
  label: string;
  value: string;
  orderBy: (item: T) => unknown;
  direction?: 'asc' | 'desc';
}

export interface AllysSortControlProps<T = unknown> {
  /**
   * Available sort options
   */
  options: SortOption<T>[];
  /**
   * Current sort value
   */
  value?: string;
  /**
   * Callback when sort changes
   */
  onChange?: (value: string) => void;
  /**
   * Label text
   */
  label?: string;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * AllysSortControl - Modern sort dropdown with Allys styling
 */
export function AllysSortControl<T = unknown>({
  options,
  value,
  onChange,
  label = 'Sort by',
  className = '',
}: AllysSortControlProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  const currentOption = options.find(opt => opt.value === value) || options[0];
  
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-3">
        {/* Label */}
        <label 
          htmlFor="allys-sort" 
          className="text-sm font-bold text-gray-700 whitespace-nowrap flex items-center gap-2 font-inter"
        >
          <svg className="w-4 h-4 text-[#BBA339]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
          </svg>
          {label}
        </label>
        
        {/* Custom Styled Select Container */}
        <motion.div
          className={`
            relative rounded-xl border-2 transition-all duration-300 bg-white
            ${isFocused 
              ? 'border-[#243462] shadow-lg shadow-[#243462]/10 ring-4 ring-[#BBA339]/10' 
              : 'border-gray-200 shadow-sm hover:border-gray-300 hover:shadow-md'
            }
          `}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          {/* Background gradient effect */}
          <div className={`
            absolute inset-0 bg-gradient-to-r from-[#243462]/5 to-[#BBA339]/5 opacity-0 transition-opacity duration-300 rounded-xl
            ${isFocused ? 'opacity-100' : ''}
          `} />
          
          {/* Sort Direction Icon */}
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <motion.svg
              className={`w-4 h-4 transition-colors ${isFocused ? 'text-[#BBA339]' : 'text-gray-500'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              animate={isFocused ? { y: [0, -2, 0] } : {}}
              transition={{ duration: 0.5, repeat: isFocused ? Infinity : 0, repeatDelay: 1 }}
            >
              {currentOption?.direction === 'desc' ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              )}
            </motion.svg>
          </div>
          
          {/* Select Element */}
          <select
            id="allys-sort"
            value={value || options[0]?.value}
            onChange={handleChange}
            onFocus={() => {
              setIsFocused(true);
              setIsOpen(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              setIsOpen(false);
            }}
            className="
              relative block appearance-none bg-transparent pl-10 pr-10 py-3 text-gray-900
              font-semibold text-sm cursor-pointer focus:outline-none min-w-[200px] font-inter
            "
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          {/* Dropdown Arrow */}
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <motion.svg
              className={`w-5 h-5 transition-colors ${isFocused ? 'text-[#243462]' : 'text-gray-500'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </motion.svg>
          </div>
          
          {/* Animated border accent */}
          {isFocused && (
            <motion.div
              layoutId="sortControlAccent"
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#243462] via-[#BBA339] to-[#E4CE4A]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              exit={{ scaleX: 0 }}
              transition={{ duration: 0.3 }}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
}

